import {
  Component,
  ViewChild,
  ViewChildren,
  QueryList,
  Output,
  EventEmitter,
  Input,
} from '@angular/core';
import { StorageService } from '../../../core/storage/storageService';
import { Options } from '../../../components/ui/filterable-table/options';
import { TableView } from '../../../components/ui/filterable-table/tableView';
import { Router } from '@angular/router';
import { InvoiceTableViewComponent } from '../invoice-table-view/invoice-table-view.component';
import { AccessManager } from '../../../components/ui/access-manager/access-manager';
import { UIErrorHandler } from '../../../core/uiErrorHandler';
import { InvoiceService } from '../../../models/invoices/invoiceService';
import { SearchFilters } from '../../../models/search/searchFilters';
import { Customer } from '../../../models/customer/customer';
import { InvoiceItem } from '../../../models/invoices/invoiceItem';
import { UIHelpers } from '../../../core/ui-helpers';
import { InvoiceSelectItemsComponent } from '../invoice-select-items/invoice-select-items.component';
import { MatDialog } from '@angular/material/dialog';
import { TextModes } from '../../ui/controls/text-input/text-input.component';
import { CatalogItem } from '../../../models/catalog/catalogItem';
import { ListItem } from '../../../../../../../libs/Shared/components/model/listItem';

@Component({
  selector: 'app-invoice-line-items',
  templateUrl: './invoice-line-items.component.html',
  standalone: false,
})
export class InvoiceLineItemsComponent {
  TextModes = TextModes;
  @Output() subtotalChange = new EventEmitter<number>();
  @Input() invoiceItems: InvoiceItem[] = [];
  @Input() readonly: boolean = false;

  invoiceSelectItemsDialog: MatDialog;
  merchantId: string | undefined;
  selectedCustomer: Partial<Customer> | undefined;
  subtotal: number = 0;
  subtotalFormatted: string = '';

  catalogItems: InvoiceItem[] = [];
  catalogItemsOptions: ListItem[] = [];

  selectedItemId: string | undefined = undefined;

  sortBy: string = '';
  sortDirection: string = '';

  constructor(
    public storage: StorageService,
    public invoiceService: InvoiceService,
    public uiHelpers: UIHelpers,
  ) {
    this.invoiceSelectItemsDialog = new MatDialog();
  }

  ngAfterViewInit() {
    this.merchantId = this.storage.getSelectedLocation()?.id;
    this.invoiceService.searchCatalogItems(this.merchantId!, '', 0, 100, ['name']).then((response) => {
      if (response) {
        this.catalogItems = response;
        for (const item of response) {
          this.catalogItemsOptions.push(new ListItem({
            id: item.id,
            name: item.name
          }));
        }
      }
    });
  }

  handleSearchChange(search: string) {
    this.invoiceService.searchCatalogItems(this.merchantId!, search.toLocaleLowerCase(), 0, 100, ['name']).then((response) => {
      if ((this.catalogItems.toString() !== response.toString()) && response.length > 0) {
        this.catalogItems = response;
        this.catalogItemsOptions = [];
        for (const item of response) {
          this.catalogItemsOptions.push(new ListItem({
            id: item.id,
            name: item.name
          }));
        }
      }
    });
  }

  setReadOnly(readonly: boolean) {
    this.readonly = readonly;
  }

  setInvoiceItems(items: InvoiceItem[]) {
    this.invoiceItems = items;
    this.updateSubtotal(true);
  }

  updateSubtotal(loadedInvoice: boolean = false) {
    this.subtotal = this.invoiceItems.reduce(
      (acc, item) => acc + item.unitPrice * item.quantity,
      0,
    );
    this.subtotalFormatted = UIHelpers.formatCurrency(this.subtotal);
    console.log('subtotalFormatted', this.subtotalFormatted);
    this.subtotalChange.emit(this.subtotal);
    console.log('subtotal', this.subtotal);
  }

  handleSelectedSearchRecord(record: any) {
    const selectedItem = this.catalogItems.find((item) => item.id === record);
    if (
      this.invoiceItems &&
      this.invoiceItems.find((item) => item.name === selectedItem?.name)
    ) {
      return;
    }
    let newItem = this.invoiceItemFromCatalogItem(selectedItem!);
    this.invoiceItems.push(newItem);
    this.updateSubtotal();
    this.selectedItemId = undefined;
  }

  handleRemoveItem(item: InvoiceItem) {
    this.invoiceItems.splice(this.invoiceItems.indexOf(item), 1);
    this.updateSubtotal();
  }

  handleQuantityChange(item: InvoiceItem, quantity: string) {
    this.invoiceItems[this.invoiceItems.indexOf(item)].quantity =
      parseInt(quantity);
    // let unitPrice = this.uiHelpers.demagnifyAmount(this.invoiceItems[this.invoiceItems.indexOf(item)].unitPrice, 100);
    // this.invoiceItems[this.invoiceItems.indexOf(item)].total = Number(UIHelpers.formatCurrency(unitPrice * parseInt(quantity)).replace('$', '').replace(',', ''));
    this.updateSubtotal();
  }

  handleAmountChange(item: InvoiceItem, amount: string) {
    this.invoiceItems[this.invoiceItems.indexOf(item)].unitPrice =
      parseFloat(amount);
    this.updateSubtotal();
  }

  handleAddNewItem() {
    let dialogRef = this.invoiceSelectItemsDialog.open(
      InvoiceSelectItemsComponent,
      {
        disableClose: false,
        autoFocus: true, // for some reason, this needs to be set even though it should default to true
        data: {
          merchantId: this.merchantId,
        },
        width: '500px',
      },
    );

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        let invoiceItem = new InvoiceItem(result.item);
        this.invoiceItems.push(invoiceItem);
        this.updateSubtotal();
      }
    });
  }

  invoiceItemFromCatalogItem(catalogItem: Partial<CatalogItem>): InvoiceItem {
    return new InvoiceItem({
      catalogItemId: catalogItem.id,
      name: catalogItem.name,
      description: catalogItem.description,
      unitPrice: catalogItem.price || 0,
      quantity: 1,
      total: catalogItem.price || 0,
    });
  }

  handleSortByChange(sortBy: string) {
    this.sortBy = sortBy;
    if (this.sortDirection == '' || this.sortDirection == 'desc') {
      this.sortDirection = 'asc';
    } else {
      this.sortDirection = 'desc';
    }

    //Sort the invoice items
    this.invoiceItems.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return this.sortDirection == 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        case 'quantity':
          return this.sortDirection == 'asc' ? a.quantity - b.quantity : b.quantity - a.quantity;
        case 'unitPrice':
          return this.sortDirection == 'asc' ? a.unitPrice - b.unitPrice : b.unitPrice - a.unitPrice;
        default:
          return 0;
      }
    });

  }
}
